import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, btnPrimary, btnSecondary } from '../styles/theme.js'
import {
  GRADE_COLORS, CRITICALITY_COLORS, FULFILLMENT_LABELS,
  buildDefaultGrades, mergeExistingGrades,
} from './eval-panel.js'

// --- Styles ---

const panelWrap = css`
  font-size: ${font.size.sm};
  overflow-y: auto;
  height: 100%;
  padding: ${space.md};
`

const rubricSection = css`
  border: 1px solid ${colors.border};
  border-radius: 6px;
  margin-bottom: ${space.md};
  overflow: hidden;
`

const rubricHeader = css`
  padding: ${space.sm} ${space.md};
  background: ${colors.bg};
  display: flex;
  align-items: center;
  gap: ${space.sm};
  font-size: ${font.size.sm};
  font-weight: ${font.weight.medium};
`

const rubricBody = css`
  padding: ${space.sm} ${space.md};
  border-top: 1px solid ${colors.border};
`

const criterionItem = css`
  padding: ${space.sm} 0;
  border-bottom: 1px solid ${colors.border};
  &:last-child { border-bottom: none; }
`

const criterionHeader = css`
  display: flex;
  align-items: flex-start;
  gap: ${space.sm};
  margin-bottom: ${space.xs};
`

const criticalityDot = css`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
`

const criterionText = css`
  flex: 1;
  line-height: 1.4;
`

const gradeRow = css`
  display: flex;
  gap: 3px;
  margin-top: ${space.xs};
  align-items: center;
`

const gradeBtn = css`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  color: ${colors.textSecondary};
  cursor: pointer;
  transition: all 0.1s ease;
  &:hover { background: ${colors.bg}; }
`

const notesInput = css`
  width: 100%;
  padding: ${space.xs} ${space.sm};
  border: 1px solid ${colors.border};
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-family: ${font.family};
  margin-top: ${space.xs};
  resize: vertical;
  min-height: 28px;
`

const modifiedBadge = css`
  font-size: 10px;
  color: ${colors.brand};
  font-weight: ${font.weight.medium};
  margin-left: auto;
  padding: 1px 6px;
  border-radius: 8px;
  background: ${colors.brand}22;
`

const autoFilledBadge = css`
  font-size: 10px;
  color: ${colors.purple};
  font-weight: ${font.weight.medium};
  margin-left: auto;
  padding: 1px 6px;
  border-radius: 8px;
  background: ${colors.purple}22;
`

const warningBanner = css`
  padding: ${space.sm} ${space.md};
  background: ${colors.important}18;
  border: 1px solid ${colors.important};
  border-radius: 6px;
  margin-bottom: ${space.md};
  font-size: ${font.size.xs};
  color: ${colors.text};
`

const versionWarning = css`
  font-size: ${font.size.xs};
  color: ${colors.important};
  padding: ${space.xs} 0;
`

const progressText = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: ${space.xs};
`

// PropagateEvalPanel — streamlined grading panel for propagation workflow.
// All rubrics are expanded by default. No add/remove rubric UI.
//
// Props:
//   sourceEvalData  - array of { evaluation, rubric } from source session
//   grades          - rubricId -> { criterionId: { fulfilled, notes } }
//   sourceGrades    - rubricId -> { criterionId: { fulfilled, notes } } (original source values)
//   onGradeChange(rubricId, criterionId, fulfilled)
//   onNotesChange(rubricId, criterionId, notes)
//   onSave()
//   onSkip()
//   saving
//   dirty
//   targetHasExistingEvals
export function PropagateEvalPanel() {
  return {
    view(vnode) {
      const {
        sourceEvalData, grades, sourceGrades, autoFilledMap,
        onGradeChange, onNotesChange,
        onSave, onSkip, saving, dirty,
        targetHasExistingEvals,
      } = vnode.attrs

      if (!sourceEvalData || sourceEvalData.length === 0) {
        return m('div', { class: panelWrap },
          m('div', { style: `color: ${colors.textMuted}; font-style: italic;` },
            'No evaluation data from source session.'))
      }

      const content = []

      if (targetHasExistingEvals) {
        content.push(m('div', { key: '__warn__', class: warningBanner },
          'This session already has evaluations. Saving will overwrite existing grades.'))
      }

      for (const { evaluation, rubric } of sourceEvalData) {
        const rubricId = rubric.id
        const criteria = rubric.criteria || []
        const rubricGrades = grades[rubricId] || {}
        const sourceRubricGrades = sourceGrades[rubricId] || {}
        const sourceVersionId = evaluation.rubric_version_id
        const currentVersionId = rubric.current_version_id
        const versionMismatch = sourceVersionId !== currentVersionId

        const bodyChildren = []

        if (versionMismatch) {
          bodyChildren.push(m('div', { key: '__vwarn__', class: versionWarning },
            `Source graded against v${evaluation.version}, current is v${rubric.versions?.[0]?.version || '?'}. Grades reset — please re-evaluate.`))
        }

        const rubricAutoFilled = autoFilledMap?.[rubricId] || {}
        const allAutoFilled = criteria.length > 0 && criteria.every(c => rubricAutoFilled[c.id] === true)
        if (allAutoFilled && !versionMismatch) {
          bodyChildren.push(m('div', {
            key: '__autofill__',
            style: `font-size: ${font.size.xs}; color: ${colors.purple}; padding: ${space.xs} 0; margin-bottom: ${space.xs};`,
          }, 'All grades auto-filled from source. Click any grade to confirm or modify.'))
        }

        for (const criterion of criteria) {
          const grade = rubricGrades[criterion.id] || { fulfilled: 'ungraded', notes: '' }
          const sourceGrade = sourceRubricGrades[criterion.id]
          const isAutoFilled = rubricAutoFilled[criterion.id] === true
          const isModified = !isAutoFilled && sourceGrade && (
            grade.fulfilled !== sourceGrade.fulfilled ||
            grade.notes !== sourceGrade.notes
          )

          bodyChildren.push(m('div', { key: criterion.id, class: criterionItem }, [
            m('div', { class: criterionHeader }, [
              m('div', {
                class: criticalityDot,
                style: `background: ${CRITICALITY_COLORS[criterion.criticality]};`,
                title: criterion.criticality,
              }),
              m('div', { class: criterionText }, criterion.description),
              isAutoFilled
                ? m('span', { class: autoFilledBadge }, 'Auto-filled')
                : isModified
                  ? m('span', { class: modifiedBadge }, 'Modified')
                  : null,
            ]),
            m('div', { class: gradeRow },
              ['yes', 'no', 'partial', 'unclear', 'na'].map(val =>
                m('button', {
                  key: val,
                  class: gradeBtn,
                  style: grade.fulfilled === val
                    ? `background: ${GRADE_COLORS[val]}; color: white; border-color: ${GRADE_COLORS[val]};${isAutoFilled ? ' border-style: dashed; opacity: 0.75;' : ''}`
                    : '',
                  onclick: () => onGradeChange(rubricId, criterion.id, val),
                }, FULFILLMENT_LABELS[val])
              ),
            ),
            m('textarea', {
              class: notesInput,
              placeholder: 'Notes...',
              value: grade.notes || '',
              oninput: (e) => onNotesChange(rubricId, criterion.id, e.target.value),
              rows: 1,
            }),
          ]))
        }

        content.push(m('div', { key: rubricId, class: rubricSection }, [
          m('div', { class: rubricHeader }, [
            m('span', rubric.title),
            m('span', { class: progressText },
              `${Object.values(rubricGrades).filter(g => g.fulfilled !== 'ungraded').length}/${criteria.length}`),
          ]),
          m('div', { class: rubricBody }, bodyChildren),
        ]))
      }

      return m('div', { class: panelWrap }, content)
    },
  }
}
